import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Control, FieldValues, Path, UseFormGetValues } from 'react-hook-form';
import { ClassifierData } from 'types/classifiers';
import { EntityTextDto } from 'types/jobPositions';
import { cn } from 'utils/cn';
import { getClassifierText, t } from 'utils/getTranslations';
import TextInput from '../rhf-inputs/TextInput';

type FormWithEntityTexts = FieldValues & { entityTexts: EntityTextDto[] | null };

type Props<TFormData extends FormWithEntityTexts> = {
    /** The message id of the title it sits under, which names the table. */
    labelId: string;
    classifiers: Record<string, ClassifierData>;
    control: Control<TFormData>;
    entityTexts: EntityTextDto[];
    getValues: UseFormGetValues<TFormData>;
    readonly?: boolean;
};

const TranslationsInput = <TFormData extends FormWithEntityTexts>({
    labelId,
    classifiers,
    control,
    entityTexts,
    getValues,
    readonly = false,
}: Props<TFormData>): JSX.Element => {
    const fieldCellClass = cn('w-1/4 align-top normal-case', !readonly && 'py-2 pr-4 pl-2');
    const headCellClass = 'h-auto w-1/4 px-4 py-2 text-left text-xs leading-5 normal-case';

    const isValidEntityText = async (value: unknown, index: number): Promise<true | string> => {
        const entityText = getValues(`entityTexts.${index}` as Path<TFormData>) as unknown as EntityTextDto;
        const errorMsg = '';

        if (value) {
            return Promise.resolve(true);
        }

        if (!entityText.value && !entityText.textMale && !entityText.textFemale) {
            return Promise.resolve(true);
        }

        if (entityText.value || (entityText.textMale && entityText.textFemale)) {
            return Promise.resolve(true);
        }

        return Promise.resolve(errorMsg);
    };

    return (
        <div className="border-divider bg-paper overflow-hidden rounded-sm border">
            <Table aria-label={t(labelId)}>
                <TableHeader className="bg-muted">
                    <TableRow className="hover:bg-transparent">
                        <TableHead className={headCellClass}>{t('translations.language')}</TableHead>
                        <TableHead className={headCellClass}>{t('translations.text')}</TableHead>
                        <TableHead className={headCellClass}>{t('translations.text-male')}</TableHead>
                        <TableHead className={headCellClass}>{t('translations.text-female')}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {entityTexts.map((field: EntityTextDto, index: number) => (
                        <TableRow key={`row-${field.langCode}`} className="align-top last:border-0">
                            <TableCell className="w-1/4 p-4 align-top normal-case">
                                {getClassifierText(classifiers['LANGUAGE'], field.langCode)}
                            </TableCell>
                            <TableCell className={fieldCellClass}>
                                <TextInput
                                    gap={0}
                                    control={control}
                                    placeholder="forms.enter-text"
                                    name={`entityTexts.${index}.value` as Path<TFormData>}
                                    readonly={readonly}
                                    validate={(val) => isValidEntityText(val, index)}
                                />
                            </TableCell>
                            <TableCell className={fieldCellClass}>
                                <TextInput
                                    gap={0}
                                    control={control}
                                    placeholder="forms.enter-text"
                                    name={`entityTexts.${index}.textMale` as Path<TFormData>}
                                    readonly={readonly}
                                    validate={(val) => isValidEntityText(val, index)}
                                />
                            </TableCell>
                            <TableCell className={fieldCellClass}>
                                <TextInput
                                    gap={0}
                                    control={control}
                                    placeholder="forms.enter-text"
                                    name={`entityTexts.${index}.textFemale` as Path<TFormData>}
                                    readonly={readonly}
                                    validate={(val) => isValidEntityText(val, index)}
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
};

export default TranslationsInput;
